"use client";

import Link from "next/link";
import { Shield, Lock, Eye, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n/I18nProvider";

interface Section {
  icon: React.ReactNode;
  titleKey: string;
  contentKey: string;
  subsections?: Array<{
    titleKey: string;
    contentKey: string;
  }>;
}

export default function PrivacyPage() {
  const { t } = useI18n();

  const sections: Section[] = [
    {
      icon: <Eye size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.privacy.section.dataCollection.title",
      contentKey: "pages.legal.privacy.section.dataCollection.content",
      subsections: [
        {
          titleKey:
            "pages.legal.privacy.section.dataCollection.personal.title",
          contentKey:
            "pages.legal.privacy.section.dataCollection.personal.content",
        },
        {
          titleKey:
            "pages.legal.privacy.section.dataCollection.organizational.title",
          contentKey:
            "pages.legal.privacy.section.dataCollection.organizational.content",
        },
        {
          titleKey: "pages.legal.privacy.section.dataCollection.technical.title",
          contentKey:
            "pages.legal.privacy.section.dataCollection.technical.content",
        },
      ],
    },
    {
      icon: <Lock size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.privacy.section.processing.title",
      contentKey: "pages.legal.privacy.section.processing.content",
    },
    {
      icon: <Shield size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.privacy.section.security.title",
      contentKey: "pages.legal.privacy.section.security.content",
    },
    {
      icon: <Users size={24} className="text-keiro-green" />,
      titleKey: "pages.legal.privacy.section.rights.title",
      contentKey: "pages.legal.privacy.section.rights.content",
    },
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            {t("pages.legal.privacy.title") || "Политика конфиденциальности"}
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            {t("pages.legal.privacy.subtitle") ||
              "Как мы собираем, обрабатываем и защищаем ваши данные"}
          </p>
          <p className="text-sm text-slate-500 mt-4">
            {t("pages.legal.privacy.lastUpdated") ||
              "Последнее обновление: 12 апреля 2026"}
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 border-t border-slate-200">
        <p className="text-lg text-slate-600 leading-relaxed mb-6">
          {t("pages.legal.privacy.intro") ||
            `ООО "Кейронт" (далее — "Компания") уважает конфиденциальность своих пользователей
            и берёт на себя обязательство защищать персональные данные в соответствии с
            Федеральным законом от 27 июля 2006 г. № 152-ФЗ "О защите персональных данных"
            и другими применимыми законами.`}
        </p>
        <p className="text-lg text-slate-600 leading-relaxed">
          {t("pages.legal.privacy.intro2") ||
            `Настоящая Политика описывает, как мы собираем, используем, обрабатываем и защищаем
            информацию при использовании платформы Keiront.`}
        </p>
      </section>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16">
        {sections.map((section, idx) => (
          <section key={idx} className="border-t border-slate-200 pt-12">
            <div className="flex gap-4 mb-6">
              <div className="flex-shrink-0 mt-1">{section.icon}</div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-4">
                  {t(section.titleKey) || section.titleKey}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed mb-6">
                  {t(section.contentKey) || section.contentKey}
                </p>

                {section.subsections && (
                  <div className="space-y-6 ml-4 border-l-2 border-keiro-green/30 pl-4">
                    {section.subsections.map((sub, subIdx) => (
                      <div key={subIdx}>
                        <h3 className="text-lg font-semibold text-slate-900 mb-2">
                          {t(sub.titleKey) || sub.titleKey}
                        </h3>
                        <p className="text-slate-600 leading-relaxed">
                          {t(sub.contentKey) || sub.contentKey}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>
        ))}

        {/* Compliance Section */}
        <section className="border-t border-slate-200 pt-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            {t("pages.legal.privacy.compliance.title") ||
              "Соответствие законодательству"}
          </h2>
          <div className="space-y-4 text-slate-600">
            <p className="leading-relaxed">
              {t("pages.legal.privacy.compliance.fz152") ||
                "Мы обрабатываем персональные данные в полном соответствии с требованиями Федерального закона № 152-ФЗ. Все данные хранятся на серверах, расположённых в Российской Федерации."}
            </p>
            <p className="leading-relaxed">
              {t("pages.legal.privacy.compliance.gdpr") ||
                "Для резидентов ЕС: Если вы находитесь в Европейском Союзе, ваши данные обрабатываются в соответствии с Общим регламентом по защите данных (GDPR)."}
            </p>
          </div>
        </section>

        {/* Contact Section */}
        <section className="border-t border-slate-200 pt-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            {t("pages.legal.privacy.contact.title") ||
              "Контакты ответственного лица"}
          </h2>
          <div className="bg-slate-50 rounded-lg p-6 border border-slate-200">
            <p className="text-slate-600 mb-4">
              {t("pages.legal.privacy.contact.desc") ||
                "Если у вас есть вопросы о нашей Политике конфиденциальности или о том, как мы обрабатываем ваши данные, пожалуйста, свяжитесь с нами:"}
            </p>
            <div className="space-y-2 font-mono text-sm text-slate-900">
              <p>Email: privacy@keiront.io</p>
              <p>
                {t("pages.legal.privacy.contact.address") ||
                  "Адрес: г. Москва, Российская Федерация"}
              </p>
            </div>
          </div>
        </section>

        {/* Changes Section */}
        <section className="border-t border-slate-200 pt-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-6">
            {t("pages.legal.privacy.changes.title") ||
              "Изменения в Политике"}
          </h2>
          <p className="text-slate-600 leading-relaxed">
            {t("pages.legal.privacy.changes.content") ||
              "Мы вправе обновлять данную Политику конфиденциальности. Уведомления об изменениях будут опубликованы на этой странице с указанием даты последнего обновления."}
          </p>
        </section>
      </div>

      {/* CTA Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-keiro-mist rounded-xl p-12 text-center border border-keiro-green/30">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            {t("pages.legal.privacy.cta.title") ||
              "Есть вопросы о конфиденциальности?"}
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            {t("pages.legal.privacy.cta.desc") ||
              "Наша команда готова помочь вам разобраться в любых вопросах"}
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Button asChild variant="default" className="h-12 px-8">
              <Link href="/contact">
                {t("pages.legal.privacy.cta.contact") ||
                  "Связаться с нами"}
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-12 px-8">
              <Link href="/legal/terms">
                {t("pages.legal.privacy.cta.terms") ||
                  "Условия использования"}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
